import React, { useCallback, useMemo } from 'react';
import cx from 'classnames';
import { Link, useParams } from 'react-router-dom';
import { UncontrolledTooltip } from 'reactstrap';
import StatusBadge from '../StatusBadge';
import { Icon } from 'js/components';
import classNames from 'classnames';
import { format } from 'date-fns';
import copy from 'copy-to-clipboard';
import { useNotify } from 'js/hooks/useNotify';

import CapabilityScoreBar from './CapabilityScoreBar';
import styles from '../UserGroup.module.scss';

import {
  faArchive,
  faCopy,
  faEdit,
  faBoxOpen,
} from '@fortawesome/pro-solid-svg-icons';

function hasResponses(score) {
  return score != null && Number.isFinite(score);
}

function formatScore(score) {
  return hasResponses(score) ? `${Math.round(score * 100)}%` : 'No data';
}

function CapabilityScoreTooltipContent({ capability, capabilityElementMap }) {
  const elements = Object.values(capabilityElementMap ?? {}).sort(
    (a, b) => a.paletteIndex - b.paletteIndex
  );
  return (
    <div className={styles['assessment-summary-capability-tooltip']}>
      <div className={styles['assessment-summary-capability-tooltip-title']}>
        {capability.name}
      </div>
      {elements.map((ce) => {
        const metrics = capability.elementMetrics?.[ce.key];
        if (!metrics) return null;
        const { normalizedScore, minScore, maxScore } = metrics;
        return (
          <div
            key={ce.key}
            className={styles['assessment-summary-capability-tooltip-element']}
          >
            <div
              className={cx(
                styles['assessment-summary-capability-tooltip-element-name'],
                `palette-chart-${capabilityElementMap[ce.key].paletteIndex}`
              )}
            >
              <span className="palette-bg"></span>
              {ce.name}
            </div>
            <div
              className={styles['assessment-summary-capability-tooltip-scores']}
            >
              {hasResponses(normalizedScore) ? (
                <>
                  Mean: {formatScore(normalizedScore)}{' '}
                  <span className="text-muted">
                    ({formatScore(minScore)} - {formatScore(maxScore)})
                  </span>
                </>
              ) : (
                'No responses'
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AssessmentSummary({
  assessment,
  capabilities,
  capabilitySets,
  capabilityElements,
  metrics,
  onUpdateAssessment,
  showActions = true,
  showArchiveRestore = true,
}) {
  const { workspaceId } = useParams();
  const runUrl = `${window.location.origin}/assessments/${assessment.assessment_id}/run`;
  const notify = useNotify();

  const handleCopyLink = useCallback(() => {
    copy(runUrl);

    notify({ type: 'success', message: 'Link copied to clipboard' });
  }, [runUrl, notify]);

  const handleArchiveAssessment = useCallback(() => {
    onUpdateAssessment(assessment.id, {
      archived_state: 'archived',
    });
  }, [onUpdateAssessment, assessment.id]);

  const handleRestoreAssessment = useCallback(() => {
    onUpdateAssessment(assessment.id, {
      archived_state: null,
    });
  }, [onUpdateAssessment, assessment.id]);

  const assessmentDate = format(
    new Date(
      assessment.went_live_at ??
        assessment.published_at ??
        assessment.inserted_at
    ),
    'MMM d, yyyy'
  );

  const capabilityElementObjects = Object.values(capabilityElements);
  const assessmentMetrics = metrics[assessment.id];
  const results = Object.entries(capabilities).reduce(
    (acc, [capabilityId, capability]) => {
      const capabilitySetId = capability.capability_set_id;
      if (!(capabilitySetId in acc)) {
        acc[capabilitySetId] = {
          ...capabilitySets[capabilitySetId],
          capabilities: [],
        };
      }

      const elementMetrics = capabilityElementObjects.reduce((ceAcc, ce) => {
        const capabilityMetricForElement =
          assessmentMetrics?.metrics?.[ce.key]?.[capabilityId];

        if (capabilityMetricForElement) {
          ceAcc[ce.key] = {
            elementKey: ce.key,
            answerDomain: capabilityMetricForElement.answerDomain,
            normalizedScore: capabilityMetricForElement.normalizedScore,
            minScore: capabilityMetricForElement.minScore,
            maxScore: capabilityMetricForElement.maxScore,
          };
        }
        return ceAcc;
      }, {});

      acc[capabilitySetId].capabilities.push({
        ...capability,
        elementMetrics,
      });

      return acc;
    },
    {}
  );

  const capabilityScoresData = useMemo(() => {
    return Object.entries(results).flatMap(([_capabilitySetId, resultSet]) => {
      return resultSet.capabilities.map((c) => {
        return {
          ...c,
        };
      });
    });
  }, [results]);

  const wasActive = useMemo(() => {
    return !!assessment?.went_live_at;
  }, [assessment?.went_live_at]);

  return (
    <div
      className={classNames(
        'mb-3',
        styles['assessment-summary'],
        styles[assessment.archived_state ?? assessment.status]
      )}
    >
      <div className={styles['assessment-summary-status-bar']}></div>
      <div className={styles['assessment-summary-content']}>
        <div className={styles['assessment-summary-header']}>
          <div className={styles['assessment-summary-status']}>
            <StatusBadge assessment={assessment} />
          </div>
          <div className={styles['assessment-summary-date']}>
            {assessment.archived_state === null && assessment.went_live_at ? (
              <Link
                to={`/w/${workspaceId}/pm/user-groups/${assessment.user_group_id}/assessments/${assessment.id}/review`}
              >
                {assessmentDate}
              </Link>
            ) : (
              assessmentDate
            )}
          </div>
          <div className={styles['assessment-user-group-name']}>
            {assessment.user_group?.name}
          </div>
        </div>
        <div className={styles['assessment-summary-details']}></div>
        {wasActive && capabilityScoresData && (
          <div className={styles['assessment-summary-capability-scores']}>
            {capabilityScoresData.map((c) => {
              const tooltipId = `capability-score-${assessment.id}-${c.id}`;
              return (
                <React.Fragment key={c.id}>
                  <div
                    id={tooltipId}
                    className={styles['assessment-summary-capability-row']}
                  >
                    <CapabilityScoreBar
                      segmentWidth={14}
                      segmentSpacing={2}
                      height={36}
                      elementMetrics={c.elementMetrics}
                      capabilityElementMap={capabilityElements}
                      capability={c}
                    />
                  </div>
                  <UncontrolledTooltip
                    placement="top"
                    target={tooltipId}
                    fade={false}
                    delay={{ show: 200, hide: 200 }}
                  >
                    <CapabilityScoreTooltipContent
                      capability={c}
                      capabilityElementMap={capabilityElements}
                    />
                  </UncontrolledTooltip>
                </React.Fragment>
              );
            })}
          </div>
        )}
        <div className={styles['assessment-summary-actions']}>
          {showActions && (
            <>
              {assessment.archived_state === null && (
                <Link
                  className="btn btn-inline btn-outline-info"
                  to={`/w/${workspaceId}/pm/user-groups/${assessment.user_group_id}/assessments/${assessment.id}/customize`}
                >
                  <Icon icon={faEdit} className="icon-left" /> Edit
                </Link>
              )}
              {assessment.archived_state === null &&
                assessment.status === 'live' && (
                  <button
                    className="btn btn-inline btn-outline-info"
                    onClick={handleCopyLink}
                  >
                    <Icon icon={faCopy} className="icon-left" /> Copy link
                  </button>
                )}
              {showArchiveRestore && assessment.archived_state === null && (
                <button
                  className="btn btn-inline btn-outline-info"
                  onClick={handleArchiveAssessment}
                >
                  <Icon icon={faArchive} className="icon-left" /> Archive
                </button>
              )}
              {showArchiveRestore && assessment.archived_state === 'archived' && (
                <button
                  className="btn btn-inline btn-outline-info"
                  onClick={handleRestoreAssessment}
                >
                  <Icon icon={faBoxOpen} className="icon-left" /> Restore
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AssessmentSummary;
